import { useCallback, useState, useEffect, useRef } from "react";
import { SelectOption } from "../../../ui/select/select";
import { API_TIMESHEET_PROJECT_CATEGORIES, GET_WORK_CATEGORIES } from "../../../../../config/Endpoints.js";
import { getApi } from "../../../../utils/api.ts"
import Cookies from "js-cookie";
import { useUser } from "../../../../lib/rbac/useUser.ts";

interface ApiProject {
  projectId: number;
  projectName: string;
  workstreams?: WorkStreamOption[];
  needsApproval?: boolean | null;
  timesheetAccessMode?: "ASSIGNED_ONLY" | "GLOBAL" | "DEPARTMENT" | null;
}

export interface WorkStreamOption {
  workStreamId: number;
  workStream: string;
}

interface ApiResponse {
  taskCategories: string[];
  projects: ApiProject[];
}

function toWorkDate(value: Date | string): string {
  if (typeof value === "string") return value.slice(0, 10);
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
}

interface ProjectCategoryOption {
  id: number;
  name: string;
  status: number;
}

export interface PhaseOption { id?: number | null; value: string; code?: string | null; label: string; allocatedHours: number; }
interface EntryOptionsResponse { phases: PhaseOption[]; categories: ProjectCategoryOption[]; phaseRequired: boolean; }

function formatCategory(val: string) {
  if (!val) return "";
  const lower = val.toLowerCase().trim();
  if (lower === "r & d" || lower === "r&d" || lower === "r - and - d" || lower === "r-and-d") return "R & D";
  return val.charAt(0).toUpperCase() + val.slice(1).toLowerCase();
}

export function useTimesheetOptions(selectedDate: Date | string) {
  const workDate = toWorkDate(selectedDate);
  const user = useUser();
  const isRegularEmployee = user?.empType?.trim().toUpperCase() === "REGULAR";
  const [categories, setCategories] = useState<SelectOption[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [phases, setPhases] = useState<PhaseOption[]>([]);
  const [projects, setProjects] = useState<SelectOption[]>([]);
  const [projectWorkstreamsMap, setProjectWorkstreamsMap] = useState<
    Record<string, WorkStreamOption[]>
  >({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const accessToken = Cookies.get("accessToken");
  const entryOptionsCache = useRef<Record<string, EntryOptionsResponse>>({});
  const entryOptionsRequest = useRef(0);
  const globalCategoriesRef = useRef<SelectOption[]>([]);

  const loadProjectCategories = useCallback(async (projectId: string, workstreamId?: string) => {
    const requestId = ++entryOptionsRequest.current;
    if (!projectId || projectId === "MISCELLANEOUS") {
      setCategoriesLoading(false);
      setCategories(globalCategoriesRef.current);
      setPhases([]);
      return;
    }
    const cacheKey = `${projectId}:${workstreamId || "PROJECT"}`;
    const cached = entryOptionsCache.current[cacheKey];
    if (cached) {
      setCategoriesLoading(false);
      setCategories(cached.categories.filter((item) => item.status === 1).map((item) => ({ label: item.name, value: String(item.id) })));
      setPhases(cached.phases ?? []);
      return;
    }
    setCategoriesLoading(true);
    setPhases([]);
    try {
      const query = workstreamId ? `?workstreamId=${encodeURIComponent(workstreamId)}` : "";
      const { data } = await getApi<EntryOptionsResponse | { data: EntryOptionsResponse }>(
        `${API_TIMESHEET_PROJECT_CATEGORIES}/${projectId}/entry-options${query}`,
        accessToken,
      );
      const raw = "data" in data ? data.data : data;
      const mapped = (raw.categories ?? []).filter((item) => item.status === 1).map((item) => ({ label: item.name, value: String(item.id) }));
      entryOptionsCache.current[cacheKey] = raw;
      if (requestId === entryOptionsRequest.current) {
        setCategories(mapped);
        setPhases(raw.phases ?? []);
      }
    } catch (err) {
      if (requestId === entryOptionsRequest.current) {
        setError(err instanceof Error ? err.message : "Failed to fetch project categories");
        setCategories([]);
        setPhases([]);
      }
    } finally {
      if (requestId === entryOptionsRequest.current) setCategoriesLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    async function fetchOptions() {
      try {
        ++entryOptionsRequest.current;
        setLoading(true);
        setError(null);
        // Project/workstream eligibility is date-specific. Clear the previous
        // day's choices before requesting options for the newly selected date.
        setProjects([]);
        setProjectWorkstreamsMap({});
        setPhases([]);
        const { data } = await getApi<ApiResponse>(
          `${GET_WORK_CATEGORIES}?date=${encodeURIComponent(workDate)}`,
          accessToken,
          "json",
          controller.signal,
        );

        if (isMounted && data) {
          const mappedCategories = data.taskCategories.map((cat: string) => ({
            label: cat.trim(),
            value: cat.trim().toLowerCase().replace(/\s+/g, "-"),
          }));

          const eligibleProjects = data.projects.filter(
            (project) => project.timesheetAccessMode !== "GLOBAL" || isRegularEmployee,
          );
          const mappedProjects = eligibleProjects.map((proj: ApiProject) => ({
            label: proj.projectName,
            value: proj.projectId.toString(),
          }));

          const workstreamsMap: Record<string, WorkStreamOption[]> = {};
          for (const proj of eligibleProjects) {
            const streams = (proj.workstreams ?? []).filter(
              (stream) =>
                Number.isFinite(Number(stream?.workStreamId)) &&
                typeof stream?.workStream === "string" &&
                stream.workStream.trim(),
            );
            if (streams.length > 0) {
              workstreamsMap[String(proj.projectId)] = streams;
            }
          }

          setCategories(mappedCategories);
          globalCategoriesRef.current = mappedCategories;
          setProjectWorkstreamsMap(workstreamsMap);
          setProjects(mappedProjects);
        }
      } catch (err) {
        if (isMounted && !(err instanceof DOMException && err.name === "AbortError")) {
          setError(err instanceof Error ? err.message : "Failed to fetch options");
          setCategories([]);
          setProjects([]);
          setProjectWorkstreamsMap({});
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    fetchOptions();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [accessToken, isRegularEmployee, workDate]);

  return { categories, phases, projects, projectWorkstreamsMap, loading, categoriesLoading, error, loadProjectCategories };
}
